// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../@types/react-modal/react-modal.d.ts" />
import { Core } from "@faire/design-tokens";
import React, { useEffect } from "react";
import ReactModal from "react-modal";
import ModalPortal from "react-modal/lib/components/ModalPortal";
import {
  useTheme,
  DefaultTheme as StyledComponentsTheme,
} from "styled-components";

import { getWindow } from "../../common/globals/getWindow";
import { IModalTheme } from "../Theme/Modal";

import { initializeReactModal } from "./reactModal";

export interface IBaseModalProps extends ReactModal.Props {
  /**
   * Use the primary color scheme for the modal.
   */
  primary?: boolean;
}

// ModalPortal is the content of the portal in the modal by default. It does
// not render a portal.
class SSRSafeModal extends ModalPortal {
  state = {
    isOpen: this.props.isOpen,
    afterOpen: false,
    beforeClose: false,
  };
}

interface IModalProps extends IBaseModalProps {
  children?: React.ReactNode;
  theme?: StyledComponentsTheme;
  noPortal?: boolean;
}

export const BaseModal: React.FC<IModalProps> = ({
  primary,
  isOpen,
  onRequestClose,
  children,
  style,
  shouldCloseOnEsc = true,
  shouldCloseOnOverlayClick = true,
  noPortal = false,
  ...otherProps
}) => {
  const theme = useTheme();

  useEffect(() => {
    if (isOpen && noPortal) {
      getWindow()
        ?.document?.getElementById?.("main")
        ?.setAttribute("aria-hidden", "true");
    }

    return () => {
      getWindow()
        ?.document?.getElementById?.("main")
        ?.removeAttribute("aria-hidden");
    };
  }, [isOpen, noPortal]);

  useEffect(() => {
    initializeReactModal();
  }, []);

  const modalTheme: IModalTheme | undefined =
    theme.modals?.[primary ? "primary" : "default"];

  const ModalComponent = noPortal ? SSRSafeModal : ReactModal;

  const noPortalProps: Partial<ReactModal.Props> = noPortal
    ? {
        // Implement behaviour & defaults implemented in Modal (rather than the inner ModalPortal).
        ariaHideApp: true,
        shouldFocusAfterRender: true,
        bodyOpenClassName: "ReactModal__Body--open",
        role: "dialog",
        closeTimeoutMS: 0,
        shouldCloseOnEsc: true,
        shouldCloseOnOverlayClick: true,
        overlayElement: (props, contentEl) => <div {...props}>{contentEl}</div>,
        contentElement: (props, children) => <div {...props}>{children}</div>,

        // Provide a warning if we are accidentally hiding the modal because it is in "main"
        overlayRef: (overlayRef: HTMLDivElement) => {
          if (overlayRef) {
            const appElement =
              getWindow()?.document?.getElementById?.("main") ?? undefined;
            if (appElement?.contains(overlayRef)) {
              throw new Error(
                '<Modal noPortal={true} /> must be rendered outside "main".'
              );
            }
          }
        },
      }
    : {};

  return (
    <ModalComponent
      {...noPortalProps}
      {...otherProps}
      isOpen={isOpen}
      style={{
        overlay: {
          background: modalTheme?.overlay ?? "rgba(0, 0, 0, 0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          ...(style?.overlay ?? {}),
        },
        content: {
          display: "flex",
          top: "auto",
          bottom: "auto",
          left: "auto",
          right: "auto",
          border: "none",
          borderRadius: "var(--slate-radius-component-default)",
          padding: "0",
          overflow: "auto",
          alignItems: "center",
          justifyContent: "center",
          WebkitOverflowScrolling: "unset",
          background: modalTheme?.background ?? Core.surface.inverse,
          color: modalTheme?.color ?? Core.text.default,
          outline: "none",
          position: "relative",
          boxShadow: modalTheme?.boxShadow,
          ...(style?.content ?? {}),
        },
      }}
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      onRequestClose={onRequestClose}
    >
      {children}
    </ModalComponent>
  );
};
